/**
 * Live-map position animator.
 *
 * Smooths the per-vehicle marker jump that happens when a fresh GPS fix
 * arrives over the WebSocket. The marker is rendered "1 position behind"
 * real time: when a new fix N arrives, the marker begins at fix N-1 and
 * animates over (N.time - (N-1).time) ms toward N, arriving exactly when
 * the NEXT fix is rendered as the new target.
 *
 * No prediction, no dead-reckoning — the animation always has a real
 * endpoint, and the duration always matches the actual inter-fix interval.
 *
 * Module-scoped state. Designed to be fed by a single React effect that
 * watches the Redux positions slice, but the engine itself is
 * framework-agnostic and could be reused on mobile.
 *
 * Snap rules (no animation, displayed jumps immediately to the new fix):
 *   - First fix the engine has ever seen for a device.
 *   - Inter-fix gap exceeds GAP_SNAP_MS (vehicle was offline / parked).
 *   - Same or older fixTime than the current target (de-dupe / out-of-order).
 *
 * Tick emission is throttled to EMIT_HZ (~15 fps) so MapView.setData runs
 * at half RAF rate — still perceptually smooth, half the GeoJSON diff work.
 */

import { lerpPosition } from './lerpPosition';

export const GAP_SNAP_MS = 120_000; // 2 minutes
export const EMIT_HZ = 15;
const EMIT_INTERVAL_MS = 1000 / EMIT_HZ;

/** @type {Map<number, {
 *    prev: object,
 *    target: object,
 *    startTime: number,
 *    duration: number,
 *    displayed: object,
 *  }>} */
const states = new Map();

/** @type {Set<() => void>} */
const listeners = new Set();

let snapshot = new Map();
let snapshotDirty = true;
let rafId = null;
let lastEmit = 0;

function now() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function buildSnapshot() {
  const next = new Map();
  for (const [id, s] of states) next.set(id, s.displayed);
  snapshot = next;
  snapshotDirty = false;
}

function notify() {
  for (const fn of listeners) fn();
}

function tick(ts) {
  let anyAnimating = false;

  for (const s of states.values()) {
    if (s.duration <= 0) continue;
    const elapsed = ts - s.startTime;
    if (elapsed >= s.duration) {
      // Settle exactly on the target so the leg ends crisply.
      s.displayed = { ...s.target, _animated: true };
      s.duration = 0;
    } else {
      const t = elapsed / s.duration;
      const lerped = lerpPosition(s.prev, s.target, t);
      // Expose the tween inputs so the snap layer can walk the marker ALONG the
      // road polyline (interpolateAlongPolyline) instead of straight-lerp-then-
      // project. Only present mid-tween; a settled marker sits on its target.
      s.displayed = {
        ...s.target, ...lerped, _animated: true,
        _animPrev: { latitude: s.prev.latitude, longitude: s.prev.longitude },
        _animTarget: { latitude: s.target.latitude, longitude: s.target.longitude },
        _animFraction: t,
      };
      anyAnimating = true;
    }
    snapshotDirty = true;
  }

  if (snapshotDirty && ts - lastEmit >= EMIT_INTERVAL_MS) {
    buildSnapshot();
    lastEmit = ts;
    notify();
  }

  if (anyAnimating) {
    rafId = requestAnimationFrame(tick);
  } else {
    // One final emit so the last frame settles to target visually even if
    // we crossed the EMIT_INTERVAL boundary on the same tick that finished.
    if (snapshotDirty) {
      buildSnapshot();
      lastEmit = ts;
      notify();
    }
    rafId = null;
  }
}

function ensureRunning() {
  if (rafId == null) {
    rafId = requestAnimationFrame(tick);
  }
}

function fixTimeMs(p) {
  if (!p || !p.fixTime) return 0;
  const t = new Date(p.fixTime).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Feed a freshly-arrived position into the engine.
 * @param {object} position  Traccar-shape position with deviceId + fixTime.
 */
export function update(position) {
  if (!position || position.deviceId == null) return;
  const id = position.deviceId;
  const existing = states.get(id);

  // First fix for this device — snap, no animation possible.
  if (!existing) {
    const snapped = { ...position, _animated: true };
    states.set(id, {
      prev: position,
      target: position,
      startTime: now(),
      duration: 0,
      displayed: snapped,
    });
    snapshotDirty = true;
    notify(); // Sync so consumers see new devices immediately on hydration.
    return;
  }

  const newT = fixTimeMs(position);
  const curT = fixTimeMs(existing.target);

  // De-dupe / out-of-order — keep current state.
  if (newT > 0 && curT > 0 && newT <= curT) return;

  const gap = newT - curT;

  // Long offline gap → snap; a vehicle reappearing across the city
  // shouldn't smoothly glide there.
  if (newT === 0 || curT === 0 || gap > GAP_SNAP_MS) {
    existing.prev = position;
    existing.target = position;
    existing.startTime = now();
    existing.duration = 0;
    existing.displayed = { ...position, _animated: true };
    snapshotDirty = true;
    notify();
    return;
  }

  // Normal leg: previous-target → new fix, animated over the real interval.
  existing.prev = existing.target;
  existing.target = position;
  existing.startTime = now();
  existing.duration = gap;
  // Keep displayed at prev — the tick loop will lerp from here.
  existing.displayed = { ...position, ...existing.prev, _animated: true };
  snapshotDirty = true;
  ensureRunning();
}

/**
 * Snap a batch of positions on first mount — Redux already has a hydration
 * snapshot when Live Map opens; we don't want to animate every marker from
 * its previous-mount position to wherever it is now.
 * @param {object[]} positions
 */
export function hydrate(positions) {
  if (!positions) return;
  for (const p of positions) {
    if (!p || p.deviceId == null) continue;
    const snapped = { ...p, _animated: true };
    states.set(p.deviceId, {
      prev: p,
      target: p,
      startTime: now(),
      duration: 0,
      displayed: snapped,
    });
  }
  snapshotDirty = true;
  buildSnapshot();
  notify();
}

/** @returns {object|null} */
export function getDisplayed(deviceId) {
  const s = states.get(deviceId);
  return s ? s.displayed : null;
}

/** @returns {ReadonlyMap<number, object>} */
export function getSnapshot() {
  if (snapshotDirty) buildSnapshot();
  return snapshot;
}

/**
 * @param {() => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Wipe all state. Called when the user toggles animation off. */
export function reset() {
  states.clear();
  if (rafId != null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  snapshot = new Map();
  snapshotDirty = false;
  lastEmit = 0;
  notify();
}

// Test helpers — not part of the public API but handy in tests.
export const __internals = {
  states,
  isRunning: () => rafId != null,
};
